import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { X } from 'lucide-react'
import toast from 'react-hot-toast'

import { AWXCardStatus } from '@ankor-io/common/types/awx'
import type { Card } from '@ankor-io/common/types/cards'

import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '@/ui-components/Dialog'
import { Link } from '@/ui-components/Link'

type FreezeCardModalProps = {
  onClose: () => void
  isOpen: boolean
  cardId: string
  onSuccess?: (updatedCard: Card) => void
}

export const FreezeCardModal = ({ onClose, isOpen = false, cardId, onSuccess }: FreezeCardModalProps) => {
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId, activeConnectedAccountId } = useUserContext()

  const handleFreezeCard = () => {
    const freezeCard = async () => {
      if (!activeOrganizationId || !activeConnectedAccountId) {
        throw new Error('Missing organization or connected account context')
      }

      const res = await fetch(
        `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/card/${cardId}/status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await getAccessToken()}`,
          },
          body: JSON.stringify({
            status: AWXCardStatus.INACTIVE,
          }),
        },
      )

      if (!res.ok) {
        const errorText = await res.text()
        throw new Error(`Failed to freeze card: ${res.status} ${res.statusText} - ${errorText}`)
      }

      // Get the updated card data
      const updatedCard: Card = await res.json()

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess(updatedCard)
      }

      // Close the modal after successful freeze
      onClose()
    }

    toast.promise(freezeCard, {
      loading: 'Freezing card...',
      success: 'Card frozen successfully',
      error: 'Failed to freeze card',
    })
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
    >
      <DialogTitle>
        <div className="flex justify-between items-center">
          <span>You're about to freeze this card</span>
          <Button
            plain
            onClick={onClose}
            aria-label="Close"
          >
            <X data-slot="icon" />
          </Button>
        </div>
      </DialogTitle>
      <DialogDescription>
        Once frozen, the card cannot be used for transactions. However, any{' '}
        <Link
          className="text-primary-600 dark:text-primary-400"
          target="_blank"
          to="https://help.airwallex.com/hc/en-gb/articles/6122441454351-What-are-Pending-or-Reversed-card-transactions"
        >
          pending transactions
        </Link>{' '}
        may still be processed.
        <br />
        <br />
        Once your card is frozen, you can unfreeze it at any time.
      </DialogDescription>
      <DialogActions>
        <Button
          color="blue"
          onClick={handleFreezeCard}
        >
          Confirm to freeze
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default FreezeCardModal
